import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { GroupChannelProvider } from '@sendbird/uikit-react/GroupChannel/context';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFetchSendBirdAppId } from '../../api/hooks/useFetchSendBirdAppId';
import { AppLoading } from '../../components/AppLoading';
import { ForwardMessageProvider } from './ForwardMessageProvider';
import { GroupCustomChannel } from './GroupCustomChannel/GroupCustomChannel';
import { GroupCustomChannelList } from './GroupCustomChannelList/GroupCustomChannelsList';
import { GroupCustomChannelSettings } from './GroupCustomChannelSettings/GroupCustomChannelSettings';
import { GroupCustomMessageSearch } from './GroupCustomMessageSearch/GroupCustomMessageSearch';
import { ConfirmDiscardChannelModal } from './ConfirmDiscardChannelModal';
import { useGetTotalUnreadMessageCount } from '../../api/hooks/sendbird/useGetTotalUnreadMessageCount';
import { GROUP_CHAT_URL_SEARCH_PARAM_KEY } from './useGoToChannel';
import { SearchControlProvider } from './GroupCustomChannelList/SearchControlContext';
import { MoonstarTitle } from '../Moonstar/MoonstarTitle';
import { getChatChannelName } from '../../utils/getChatChannelName';
import { useCurrentUser } from '../../auth/currentUserContext';
import { useSidebar } from '../../components/ui/sidebar';

export const GroupsCustom = () => {
  const { isPending: isSendBirdAppPending } = useFetchSendBirdAppId();
  const currentUser = useCurrentUser();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentChannelUrl = useMemo(
    () => searchParams.get(GROUP_CHAT_URL_SEARCH_PARAM_KEY) ?? undefined,
    [searchParams]
  );

  const [currentChannel, setCurrentChannel] = useState<GroupChannel | null>();

  const [targettedSelectedChannel, setTargettedSelectedChannel] =
    useState<GroupChannel | null>();
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { setOpen } = useSidebar();
  const [openConfirmDiscardChannel, setOpenConfirmDiscardChannel] =
    useState(false);
  const { data: unreadCount } = useGetTotalUnreadMessageCount();
  const goToChannel = useCallback(
    (channel: GroupChannel | null) => {
      setSearchParams((prev) => {
        if (channel) {
          prev.set(GROUP_CHAT_URL_SEARCH_PARAM_KEY, channel.url);
        } else {
          prev.delete(GROUP_CHAT_URL_SEARCH_PARAM_KEY);
        }
        return prev;
      });
      setCurrentChannel(channel);
    },
    [setSearchParams]
  );

  const handleSetShowSearch = (show: boolean) => {
    setShowSearch(show);
    if (show) {
      setShowSettings(false);
    }
  };

  const handleSetShowSettings = (show: boolean) => {
    setShowSettings(show);
    if (show) {
      setShowSearch(false);
    }
  };

  const handleDiscardChannel = () => {
    if (targettedSelectedChannel) {
      goToChannel(targettedSelectedChannel);
    }
    setOpenConfirmDiscardChannel(false);
  };

  const handleClose = () => {
    setOpenConfirmDiscardChannel(false);
  };

  const breadcrumb = useMemo(() => {
    const breadcrumb: string[] = [];
    breadcrumb.push((unreadCount ? `(${unreadCount}) ` : '') + 'Chat');

    if (!currentChannel) {
      return breadcrumb;
    }

    breadcrumb.push(getChatChannelName(currentChannel, currentUser));
    return breadcrumb;
  }, [currentChannel, currentUser, unreadCount]);

  if (isSendBirdAppPending) {
    return <AppLoading />;
  }

  return (
    <>
      <MoonstarTitle breadcrumb={breadcrumb} />
      <ForwardMessageProvider>
        <div className="flex h-full min-h-0 w-full">
          <SearchControlProvider>
            <GroupCustomChannelList
              onChannelSelect={(channel) => {
                // Block moving from channel to channel if there are no messages in the current one
                if (currentChannel && !currentChannel.lastMessage) {
                  setTargettedSelectedChannel(channel);
                  setOpenConfirmDiscardChannel(true);
                  return;
                }

                // If there are messages in the current channel, just switch to the new one
                goToChannel(channel);
              }}
              onChannelCreated={goToChannel}
              selectedChannelUrl={currentChannelUrl}
            />
          </SearchControlProvider>

          {currentChannelUrl && (
            <GroupChannelProvider channelUrl={currentChannelUrl}>
              {/* Current channel display */}

              <GroupCustomChannel
                onChatHeaderActionClick={() => {
                  handleSetShowSettings(true);
                  setOpen(false);
                }}
                onSearchClick={() => {
                  handleSetShowSearch(true);
                  setOpen(false);
                }}
              />

              {/* Side Panel for Settings */}
              {showSettings && !showSearch && (
                <GroupCustomChannelSettings
                  channelUrl={currentChannelUrl}
                  onCloseClick={() => handleSetShowSettings(false)}
                />
              )}

              {/* Side Panel for Search */}
              {showSearch && !showSettings && (
                <GroupCustomMessageSearch
                  channelUrl={currentChannelUrl}
                  onCloseClick={() => handleSetShowSearch(false)}
                />
              )}
            </GroupChannelProvider>
          )}
        </div>
      </ForwardMessageProvider>

      <ConfirmDiscardChannelModal
        open={openConfirmDiscardChannel}
        onClose={handleClose}
        onDiscard={handleDiscardChannel}
      />
    </>
  );
};
